import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import { api, formatPrice } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useI18n } from "../i18n/I18nContext";

export default function CoutureDocument() {
    const { id } = useParams();
    const { t } = useI18n();
    const { site } = useSite();
    const [doc, setDoc] = useState(null);

    useEffect(() => {
        api.get(`/couture/documents/${id}`).then((r) => setDoc(r.data)).catch(() => {});
    }, [id]);

    if (!doc) {
        return <div className="min-h-screen flex items-center justify-center text-gray-400">{t("admin.loading")}</div>;
    }

    const company = site?.contact || {};
    const isDevis = doc.doc_type === "devis";
    const balance = Math.max(0, (doc.price_fcfa || 0) - (doc.deposit_fcfa || 0));
    const profit = doc.cost_fcfa != null ? (doc.price_fcfa || 0) - (doc.cost_fcfa || 0) : null;

    return (
        <div className="min-h-screen bg-gray-100 py-10 px-4 print:bg-white print:py-0" data-testid="couture-document">
            <div className="max-w-3xl mx-auto">
                <div className="flex justify-between items-center mb-6 print:hidden">
                    <Link to={`/couturier/commandes/${doc.order_id}`} className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-brand-gold">
                        <ArrowLeft className="w-4 h-4" /> {t("couture.back_to_order")}
                    </Link>
                    <button
                        onClick={() => window.print()}
                        data-testid="couture-doc-print"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-navy hover:bg-brand-gold text-white text-[0.7rem] tracking-[0.2em] uppercase font-semibold transition-colors"
                    >
                        <Printer className="w-4 h-4" /> {t("couture.print")}
                    </button>
                </div>

                <div className="bg-white shadow-lg p-10 md:p-14 print:shadow-none">
                    {/* Header */}
                    <div className="flex items-start justify-between border-b-2 border-brand-gold pb-8 mb-8">
                        <Logo />
                        <div className="text-right">
                            <div className="font-display text-3xl text-brand-navy uppercase">
                                {isDevis ? t("couture.doc_devis") : t("couture.doc_facture")}
                            </div>
                            <div className="font-mono text-sm text-brand-gold-deep mt-1">{doc.doc_number}</div>
                            <div className="text-xs text-gray-400 mt-1">
                                {new Date(doc.created_at).toLocaleDateString("fr-FR")}
                            </div>
                        </div>
                    </div>

                    {/* Parties */}
                    <div className="grid grid-cols-2 gap-8 mb-10 text-sm">
                        <div>
                            <div className="text-[0.62rem] tracking-[0.2em] uppercase text-gray-400 mb-2">{t("couture.from")}</div>
                            <div className="font-display text-lg text-brand-navy">GAB'S SERVICES SARL MALI</div>
                            {company.address && <div className="text-gray-500">{company.address}</div>}
                            {company.phone && <div className="text-gray-500">{company.phone}</div>}
                        </div>
                        <div>
                            <div className="text-[0.62rem] tracking-[0.2em] uppercase text-gray-400 mb-2">{t("couture.bill_to")}</div>
                            <div className="font-semibold text-brand-navy">{doc.client_name}</div>
                            {doc.client_phone && <div className="text-gray-500">{doc.client_phone}</div>}
                        </div>
                    </div>

                    {/* Items */}
                    <table className="w-full text-sm mb-8">
                        <thead>
                            <tr className="text-left text-[0.62rem] tracking-[0.18em] uppercase text-gray-400 border-b border-gray-200">
                                <th className="py-3">{t("couture.item")}</th>
                                <th className="py-3 text-right">{t("couture.price")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-gray-100">
                                <td className="py-4">
                                    <div className="font-semibold text-brand-navy">
                                        {doc.category_name || t("couture.custom_order")}
                                        {doc.subcategory_name ? ` / ${doc.subcategory_name}` : ""}
                                    </div>
                                    {doc.description && <div className="text-gray-500 mt-1">{doc.description}</div>}
                                </td>
                                <td className="py-4 text-right font-semibold text-brand-navy">{formatPrice(doc.price_fcfa, "FCFA")}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Totals */}
                    <div className="flex justify-end">
                        <div className="w-64 text-sm">
                            <div className="flex justify-between py-2">
                                <span className="text-gray-500">{t("couture.price")}</span>
                                <span className="font-semibold text-brand-navy">{formatPrice(doc.price_fcfa, "FCFA")}</span>
                            </div>
                            {doc.price_eur != null && (
                                <div className="flex justify-between py-2 text-gray-400">
                                    <span>{t("couture.price_eur")}</span>
                                    <span>{formatPrice(doc.price_eur, "EUR")}</span>
                                </div>
                            )}
                            <div className="flex justify-between py-2 border-t border-gray-200">
                                <span className="text-gray-500">{t("couture.deposit")}</span>
                                <span className="text-emerald-600 font-semibold">{formatPrice(doc.deposit_fcfa, "FCFA")}</span>
                            </div>
                            <div className="flex justify-between py-3 border-t-2 border-brand-gold">
                                <span className="font-bold text-brand-navy uppercase text-xs tracking-wider">{t("couture.balance")}</span>
                                <span className="font-display text-xl text-brand-gold-deep">{formatPrice(balance, "FCFA")}</span>
                            </div>
                            {profit != null && (
                                <div className="flex justify-between py-2 mt-2 border-t border-dashed border-gray-200" data-testid="couture-doc-profit">
                                    <span className="text-gray-500">{t("couture.profit")}</span>
                                    <span className={`font-semibold ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatPrice(profit, "FCFA")}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-12 text-center text-xs text-gray-400 border-t border-gray-100 pt-6">
                        {isDevis ? t("couture.devis_footer") : t("couture.facture_footer")}
                    </div>
                </div>
            </div>
        </div>
    );
}
